import { useChatStomp } from '@/contexts/chat-stomp-context';
import { useChat } from '@/contexts/ChatContext';
import { useIsMobileOrTablet } from '@/hooks/use-mobile';
import { mapWebsocketEventToFbMessage, parseMediaReadyEvent } from '@/services/chat-message-normalizer';
import { FbConversation, FBMessage } from '@/types/fb-type';
import { ArrowDown, ArrowLeft, RefreshCw, ShoppingBag } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import OrderDetailsChatPanel from '@/components/orders/OrderDetailsChatPanel';
import ChatBubbleMsg from './ChatBubbleMsg';
import ChatInput from './ChatInput';
import { useChatWindowScroll } from './use-chat-window-scroll';

interface ChatWindowProp {
  currentConversation: FbConversation | null;
  messages: FBMessage[];
  isLoadingMessage: boolean;
  isLoadingMore?: boolean;
  prevCursor: string | null;
  OnNewMessage?: (message: FBMessage) => void;
  OnLoadMoreMessage?: (after: string) => void;
  hideHeader?: boolean;
  onBack?: () => void;
  inactive?: boolean;
}

type DateSeparatorItem = { type: 'date-separator'; id: string; label: string };
type MessageItem = { type: 'message'; id: string; message: FBMessage };
type TimelineItem = DateSeparatorItem | MessageItem;

function formatDateLabel(date: Date) {
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startToday.getTime() - targetDay.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function buildTimelineItems(messages: FBMessage[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let lastDateKey = '';

  for (const message of messages) {
    const createdAt = message.getCreatedAt();
    const dateKey = `${createdAt.getFullYear()}-${createdAt.getMonth()}-${createdAt.getDate()}`;
    if (dateKey !== lastDateKey) {
      items.push({
        type: 'date-separator',
        id: `date-${dateKey}`,
        label: formatDateLabel(createdAt),
      });
      lastDateKey = dateKey;
    }

    items.push({
      type: 'message',
      id: message.id,
      message,
    });
  }

  return items;
}

export default function ChatWindow({
  currentConversation,
  OnNewMessage,
  messages,
  isLoadingMessage,
  isLoadingMore = false,
  prevCursor,
  OnLoadMoreMessage,
  hideHeader,
  onBack,
  inactive = false,
}: ChatWindowProp) {
  const isMobileOrTablet = useIsMobileOrTablet();
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const { refreshMessages, reloadConversationMessages } = useChat();
  const { subscribe, isConnected } = useChatStomp();
  const {
    scrollRef,
    contentRef,
    onDivScroll,
    onUserScroll,
    showScrollChip,
    unseenCount,
    handleScrollChipClick,
  } = useChatWindowScroll({
    conversationId: currentConversation?.id,
    messages,
    isLoadingMessage,
    isLoadingMore,
    prevCursor,
    onLoadMoreMessage: OnLoadMoreMessage,
  });

  const onSendMessage = (success: boolean, _id: string, m?: FBMessage) => {
    if (success && m && OnNewMessage) OnNewMessage(m);
  };

  const handleRefreshMessages = async () => {
    if (inactive || isManualRefreshing) return;
    setIsManualRefreshing(true);
    try {
      await refreshMessages(true);
    } finally {
      setIsManualRefreshing(false);
    }
  };

  const onNewMessageRef = useRef(OnNewMessage);
  useEffect(() => {
    onNewMessageRef.current = OnNewMessage;
  }, [OnNewMessage]);

  useEffect(() => {
    if (inactive || !currentConversation?.id || !isConnected) {
      return;
    }

    const destination = `/topic/conversations/${currentConversation.id}`;
    const unsubscribe = subscribe(destination, (message) => {
      try {
        const mediaReady = parseMediaReadyEvent(message.body);
        if (mediaReady) {
          // Context ignores it if that conversation is no longer the open one.
          void reloadConversationMessages(mediaReady.conversationId);
          return;
        }
        const fbMessage = mapWebsocketEventToFbMessage(message.body);
        if (fbMessage) {
          onNewMessageRef.current?.(fbMessage);
        }
      } catch (error) {
        console.warn('Unable to parse conversation websocket event', error);
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [currentConversation?.id, inactive, isConnected, subscribe, reloadConversationMessages]);

  if (!currentConversation) return null;

  const user = currentConversation.getUser();
  const displayName = user?.name || 'Unknown';
  const avatarInitial = displayName.trim().charAt(0)?.toUpperCase() || '?';
  const isOnline = currentConversation.isOnline();

  const visibleMessages = messages.filter((msg) => {
    const hasText = typeof msg.message === 'string' && msg.message.trim().length > 0;
    const hasSticker = typeof msg.sticker === 'string' && msg.sticker.trim().length > 0;
    const hasAttachments = msg.attachments && msg.attachments.length > 0;
    return hasText || hasSticker || hasAttachments;
  });
  const timelineItems = useMemo(() => buildTimelineItems(visibleMessages), [visibleMessages]);

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden">
      {/* Header */}
      {!hideHeader && (
        <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-3 bg-white z-10">
          <div className="flex items-center gap-3 min-w-0">
            {isMobileOrTablet && onBack && (
              <button
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                aria-label="Back to chat list"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="relative flex-shrink-0">
                {user?.profileImageUrl ? (
                  <img
                    className="w-10 h-10 rounded-full object-cover"
                    src={user.profileImageUrl}
                    alt={displayName}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium text-sm">
                    {avatarInitial}
                  </div>
                )}
                {isOnline && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">{displayName}</h3>
                {isOnline && (
                  <p className="text-xs text-green-600">Active now</p>
                )}
              </div>
            </div>
            {/* The right-hand order panel is only visible on xl+, so expose it here below that */}
            <button
              type="button"
              onClick={() => void handleRefreshMessages()}
              disabled={inactive || isManualRefreshing || isLoadingMessage}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none text-sm font-medium transition-colors"
              aria-label="Refresh recent conversation messages and media"
              title="Refresh recent messages and media"
            >
              <RefreshCw className={`w-4 h-4 ${isManualRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              type="button"
              onClick={() => setIsOrderDialogOpen(true)}
              className="xl:hidden flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-medium transition-colors"
              aria-label="View orders"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Orders</span>
            </button>
          </div>
        </div>
      )}

      <Dialog open={isOrderDialogOpen} onOpenChange={setIsOrderDialogOpen}>
        <DialogContent className="p-0 gap-0 w-[95vw] max-w-md h-[90vh] sm:h-[85vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Customer orders</DialogTitle>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {user && <OrderDetailsChatPanel currentCustomer={user} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Messages scroll area */}
      <div className="relative flex-1 min-h-0">
        <div
          id="chat-scroll-area"
          ref={scrollRef}
          onScroll={onDivScroll}
          onWheel={onUserScroll}
          onTouchStart={onUserScroll}
          onPointerDown={onUserScroll}
          onKeyDown={onUserScroll}
          className="h-full overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 [overflow-anchor:none]"
          aria-live="polite"
        >
          <div ref={contentRef} className="space-y-1">
            {/* Load-more indicator at top */}
            {(isLoadingMore || (isLoadingMessage && visibleMessages.length === 0)) && (
              <div className="flex justify-center py-2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
              </div>
            )}

            {timelineItems.map((item) => (
              item.type === 'date-separator' ? (
                <div key={item.id} className="flex items-center justify-center py-2">
                  <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    {item.label}
                  </span>
                </div>
              ) : (
                <ChatBubbleMsg
                  key={item.id}
                  message={item.message}
                  currentConversation={currentConversation}
                />
              )
            ))}
          </div>
        </div>

        {/* Scroll-to-bottom chip */}
        {showScrollChip && (
          <button
            onClick={handleScrollChipClick}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full shadow-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all z-20"
          >
            <ArrowDown className="w-3.5 h-3.5 text-blue-500" />
            {unseenCount > 0 ? (
              <span>
                {unseenCount} new message{unseenCount > 1 ? 's' : ''}
              </span>
            ) : (
              <span>Scroll to bottom</span>
            )}
          </button>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-gray-200">
        {inactive ? (
          <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            <p className="text-sm font-semibold">Inactive conversation</p>
            <p className="mt-1 text-xs">
              This order was created through another Facebook Page. Showing cached messages only;
              live updates and replies are disabled.
            </p>
          </div>
        ) : (
          <ChatInput
            userId={currentConversation.getUser()!.id}
            userName={displayName}
            messagingWindow={currentConversation.messagingWindow}
            enableHumanAgent={currentConversation.enableHumanAgent}
            onSendMessage={onSendMessage}
            conversationId={currentConversation.id}
          />
        )}
      </div>
    </div>
  );
}
